import React, { useState } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import Image from 'components/common/Image';
import LoggedOutFavoritePopUp from 'components/common/EventCard/LoggedOutFavoritePopUp';
import useLoggedOutFavoriteEvent from 'hooks/useLoggedOutFavoriteEvent';
import { useModal } from 'hooks';
import useAuthProps from 'hooks/useAuthProps';
import { Box, ClickAwayListener, Fade, Popper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import ShareCalendarIcon from 'components/svg/icon-share-calendar.svg';
import ImageGalleryDialog from '../event/ImageGalleryDialog';
import EventInfoV2 from './EventInfoV2';
import ShareMenu from '../modals/ShareMenu';
import Fab from '../common/Fab';
import AddToCalendarMenu from '../event/addToCalendarMenu/AddToCalendarMenu';

interface EventV2HeadProps {
  event: any;
  onToggleFavoriteEvent?: (id: number) => void;
  frame?: boolean;
  hasUserInternalPassport?: boolean;
}

const EventV2Head: React.FC<React.PropsWithChildren<EventV2HeadProps>> = ({
  event,
  onToggleFavoriteEvent,
  frame,
  hasUserInternalPassport,
}) => {
  const { id, images = [], videos = [], user_favorite } = event;

  const files = images
    .concat(Array.isArray(videos) && videos.length > 0 ? videos : [])
    .sort(({ order: aOrder }: any, { order: bOrder }: any) => aOrder - bOrder);
  const [showImageGallery] = useModal(ImageGalleryDialog);
  const { isAuthenticated, onLogin } = useAuthProps();
  const [open, setOpen] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(false);
  const FabIcon = user_favorite ? FavoriteIcon : FavoriteBorderIcon;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [calendarEl, setCalendarEl] = React.useState<null | HTMLElement>(null);

  const handleShareMenuClick = (event: any) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
    if (open) setOpenCalendar(false);
    event.preventDefault();
  };

  const handleCalendarClick = (event: any) => {
    setCalendarEl(event.currentTarget);
    setOpenCalendar(!openCalendar);
    if (openCalendar) setOpen(false);
    event.preventDefault();
  };

  const { loggedOutFavoriteEvent, setLoggedOutFavoriteEvent, handleResetLoggedOutFavoriteEvent } =
    useLoggedOutFavoriteEvent();

  const handleImageClick = (file: File) => {
    const index = files.indexOf(file);
    showImageGallery({
      files,
      initialIndex: index,
    });
  };

  const handleToggleFavoriteEvent = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAuthenticated) {
      onToggleFavoriteEvent(id);
    } else {
      setLoggedOutFavoriteEvent(id);
    }
  };

  return (
    <EventInfoV2
      frame={frame}
      event={event}
      categoryTag
      onMainImageClick={handleImageClick}
      isPopUpToggled={loggedOutFavoriteEvent === event.id}
      isMainPage
      hasUserInternalPassport={hasUserInternalPassport}
      imagePopUp={
        <Image
          variant="rectangle"
          rounded
          alt={event.title}
          placeholder={<LoggedOutFavoritePopUp small onClose={handleResetLoggedOutFavoriteEvent} onLogin={onLogin} />}
        />
      }
      like={
        <Fab id="favorite-event-button" elevation={1} onClick={handleToggleFavoriteEvent}>
          <FabIcon color="secondary" />
        </Fab>
      }
      share={
        <Fab id="share-event-button" elevation={1} onClick={handleShareMenuClick} onTouchEnd={handleShareMenuClick}>
          {open ? <CloseIcon color="primary" /> : <ShareIcon color="primary" />}
        </Fab>
      }
      calendar={
        <Fab id="favorite-event-button" elevation={1} onClick={handleCalendarClick} onTouchEnd={handleCalendarClick}>
          <ShareCalendarIcon />
        </Fab>
      }
    >
      <Popper id={id} open={open} anchorEl={anchorEl} placement="bottom-end" style={{ zIndex: 200 }}>
        <Box
          sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}
          style={{ boxShadow: '0px 4px 8px rgba(60, 85, 165, 0.16)' }}
        >
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Fade in={open}>
              <div>
                <ShareMenu event={event} open={open} />
              </div>
            </Fade>
          </ClickAwayListener>
        </Box>
      </Popper>
      <Popper
        sx={{ zIndex: 200, height: 166 }}
        id={event.id}
        open={openCalendar}
        anchorEl={calendarEl}
        placement="bottom-start"
      >
        <Box
          sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}
          style={{ boxShadow: '0px 4px 8px rgba(60, 85, 165, 0.16)' }}
        >
          <ClickAwayListener onClickAway={() => setOpenCalendar(false)}>
            <Fade in={openCalendar}>
              <div>
                <AddToCalendarMenu event={event} open={openCalendar} />
              </div>
            </Fade>
          </ClickAwayListener>
        </Box>
      </Popper>
    </EventInfoV2>
  );
};

export default EventV2Head;
